import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, TimeScale, ChartOptions, ChartData, Plugin } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-moment';
import moment from 'moment';
import { getTimeSeriesData, getAnnotationPeriods, getTahmoQCFlagged } from '../../services/api.service';
import { DripClogPeriod } from '../../types/drip_clog.type';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, TimeScale, zoomPlugin);

interface TimeSeriesDataPoint {
    timestamp: string;
    value: number | null;
}

interface TimeSeriesData {
    data: TimeSeriesDataPoint[];
    metadata: {
        station_id: string;
        variable: string;
        start_date: string;
        end_date: string;
        count: number;
        total_count: number;
    };
}

interface QCPeriod {
    start: string;
    end: string;
    text: string;
    count: number;
    variable: string;
}

interface PrecipitationChartProps {
    stationId: string;
    startDate: string;
    endDate: string;
    height?: number;
    title?: string;
    showDripClog?: boolean;
     showQCIssues?: boolean;
}

const PrecipitationChart: React.FC<PrecipitationChartProps> = ({
    stationId,
    startDate,
    endDate,
    height = 400,
    title = 'Precipitation',
    showDripClog = false,
    showQCIssues = false
}) => {
    // State management
    const [precipitationData, setPrecipitationData] = useState<TimeSeriesData | null>(null);
    const [dripClogPeriods, setDripClogPeriods] = useState<DripClogPeriod[]>([]);
     const [dripClogPeriodsCache, setDripClogPeriodsCache] = useState<DripClogPeriod[]>([]);
    const [qcPeriods, setQCPeriods] = useState<QCPeriod[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [showDripClogAnnotations, setShowDripClogAnnotations] = useState<boolean>(showDripClog);
    const [showQCAnnotations, setShowQCAnnotations] = useState<boolean>(showQCIssues);
    const [chartKey, setChartKey] = useState<number>(0);

    const chartRef = useRef<any>(null);

     // Reset zoom function that properly accesses the chart instance
    const resetZoom = () => {
        if (chartRef.current) {
            // Access the chart instance through the react-chartjs-2 wrapper
            const chartInstance = chartRef.current;
            if (chartInstance && chartInstance.resetZoom) {
                chartInstance.resetZoom();
            } else if (chartInstance && chartInstance.chart && chartInstance.chart.resetZoom) {
                chartInstance.chart.resetZoom();
            }
        }
    };

    // Data fetching effect
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                const promises = [
                    getTimeSeriesData(stationId, 'pr', startDate, endDate)
                ];

                 // Only fetch QC data if showQC is true
                if (showQCIssues) {
                    promises.push(getTahmoQCFlagged(stationId, 'pr'));
                }

                const results = await Promise.allSettled(promises);

                // Handle precipitation data
                if (results[0].status === 'fulfilled') {
                    setPrecipitationData(results[0].value);
                } else {
                    throw new Error('Failed to fetch precipitation data');
                }

                let resultIndex = 1;

                // Handle QC data if requested
                if (showQCIssues && results.length > resultIndex) {
                    const qcResult = results[resultIndex];
                    if (results[resultIndex].status === 'fulfilled') {
                        const qcResponse = (qcResult as PromiseFulfilledResult<any>).value;

                        let qcArray = [];
                        if (Array.isArray(qcResponse)) {
                            qcArray = qcResponse;
                        } else if (qcResponse && Array.isArray(qcResponse.data)) {
                            qcArray = qcResponse.data;
                        }
                        setQCPeriods(qcArray);
                    } else {
                        setQCPeriods([]);
                    }
                } else {
                    setQCPeriods([]);
                }


            } catch (err) {
                setError('Failed to fetch precipitation data');
                console.error('Error fetching precipitation data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();

    }, [stationId, startDate, endDate, showDripClog, showQCIssues]);

    // Separate effect for drip clog periods using generic annotation API
    useEffect(() => {
        const fetchDripClogPeriods = async () => {
            if (!showDripClog) {
                setDripClogPeriods([]);
                return;
            }

            try {
                const response = await getAnnotationPeriods(stationId, 'drip_clog_periods', startDate, endDate);
                const periods = response.periods || [];

                // Always cache the fetched data
                setDripClogPeriodsCache(periods);
                setDripClogPeriods(periods);
            } catch (err) {
                console.error('Failed to fetch drip clog periods:', err);
                setDripClogPeriodsCache([]);
                setDripClogPeriods([]);
            }
        };

        fetchDripClogPeriods();
    }, [stationId, startDate, endDate, showDripClog]);

    // Effect to handle toggle changes using cached data
    useEffect(() => {
        if (showDripClog) {
            setDripClogPeriods(dripClogPeriodsCache);
        } else {
            setDripClogPeriods([]);
        }
        setShowDripClogAnnotations(showDripClog);
        setChartKey(prev => prev + 1);
    }, [showDripClog, dripClogPeriodsCache]);

        // QC annotation plugin
    const qcAnnotationPlugin: Plugin<'bar'> = useMemo(() => ({
        id: 'qcAnnotation',
        beforeDatasetsDraw: (chart) => {
            if (!showQCAnnotations || !qcPeriods.length) {
                return;
            }

            const ctx = chart.ctx;
            const chartArea = chart.chartArea;
            const xScale = chart.scales.x;

            qcPeriods.forEach((period, index) => {
                // Convert QC period dates to timestamps
                const periodStartTime = moment(period.start).valueOf();
                const periodEndTime = moment(period.end).valueOf();

                // Get pixel positions using the time scale
                const startX = xScale.getPixelForValue(periodStartTime);
                const endX = xScale.getPixelForValue(periodEndTime);

                // Check if positions are valid and within chart area
                const isValidPosition = !isNaN(startX) && !isNaN(endX) &&
                                      startX <= chartArea.right && endX >= chartArea.left;

                if (isValidPosition) {
                    // Calculate width with minimum for visibility
                    const width = Math.max(Math.abs(endX - startX), 10);
                    const actualStartX = Math.max(Math.min(startX, endX), chartArea.left);

                    // Draw background rectangle (orange/yellow for QC issues)
                    ctx.save();
                    ctx.fillStyle = 'rgba(255, 165, 0, 0.3)'; // Orange background
                    ctx.fillRect(actualStartX, chartArea.top, width, chartArea.bottom - chartArea.top);

                    // Draw border
                    ctx.strokeStyle = '#ff8c00'; // Darker orange
                    ctx.lineWidth = 2;
                    ctx.setLineDash([3, 3]);
                    ctx.strokeRect(actualStartX, chartArea.top, width, chartArea.bottom - chartArea.top);

                    // Draw label
                    ctx.setLineDash([]);
                    ctx.fillStyle = '#ff8c00';
                    ctx.font = 'bold 10px Arial';
                    ctx.textAlign = 'center';
                    const textX = actualStartX + width / 2;

                    // Draw background for text
                    const labelText = 'TAHMO Precip Flagged';
                    const textWidth = ctx.measureText(labelText).width;
                    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
                    ctx.fillRect(textX - textWidth/2 - 3, chartArea.top + 25, textWidth + 6, 16);

                    // Draw text
                    ctx.fillStyle = '#ff8c00';
                    ctx.fillText(labelText, textX, chartArea.top + 36);
                    ctx.restore();
                }
            });
        }
    }), [showQCAnnotations, qcPeriods]);


    // Drip clog annotation plugin (unchanged)
    const dripClogAnnotationPlugin: Plugin<'bar'> = useMemo(() => ({
        id: 'dripClogAnnotation',
        beforeDatasetsDraw: (chart) => {
            if (!showDripClogAnnotations || !dripClogPeriods.length) {
                return;
            }

            const ctx = chart.ctx;
            const chartArea = chart.chartArea;
            const xScale = chart.scales.x;

            dripClogPeriods.forEach((period, index) => {
                // Convert drip clog period dates to timestamps for comparison with chart data
                const periodStartTime = moment(period.start).valueOf();
                const periodEndTime = moment(period.end).valueOf();

                // Get pixel positions using the time scale
                const startX = xScale.getPixelForValue(periodStartTime);
                const endX = xScale.getPixelForValue(periodEndTime);

                // Check if positions are valid and within chart area
                const isValidPosition = !isNaN(startX) && !isNaN(endX) &&
                                      startX <= chartArea.right && endX >= chartArea.left;

                if (isValidPosition) {
                    // Calculate width with minimum for visibility
                    const width = Math.max(Math.abs(endX - startX), 5);
                    const actualStartX = Math.max(Math.min(startX, endX), chartArea.left);

                    // Draw background rectangle
                    ctx.save();
                    ctx.fillStyle = 'rgba(255, 0, 0, 0.3)';
                    ctx.fillRect(actualStartX, chartArea.top, width, chartArea.bottom - chartArea.top);

                    // Draw border
                    ctx.strokeStyle = '#ff0000';
                    ctx.lineWidth = 2;
                    ctx.setLineDash([5, 5]);
                    ctx.strokeRect(actualStartX, chartArea.top, width, chartArea.bottom - chartArea.top);

                    // Draw label
                    ctx.setLineDash([]);
                    ctx.fillStyle = '#ff0000';
                    ctx.font = 'bold 12px Arial';
                    ctx.textAlign = 'center';
                    const textX = actualStartX + width / 2;

                    // Draw background for text
                    const labelText = 'Drip Clog';
                    const textWidth = ctx.measureText(labelText).width;
                    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
                    ctx.fillRect(textX - textWidth/2 - 4, chartArea.top + 5, textWidth + 8, 20);

                    // Draw text
                    ctx.fillStyle = '#ff0000';
                    ctx.fillText(labelText, textX, chartArea.top + 18);
                    ctx.restore();
                }
            });
        }
    }), [showDripClogAnnotations, dripClogPeriods]);


    // Chart data configuration
    const chartData: ChartData<'bar'> = useMemo(() => {
        if (!precipitationData?.data) {
            return {
                labels: [],
                datasets: []
            };
        }

        const filteredData = precipitationData.data.filter(point => point.value !== null);

        return {
           labels: filteredData.map(point => moment(point.timestamp).valueOf()),
            datasets: [{
                label: 'Precipitation',
                data: filteredData.map(point => point.value),
                backgroundColor: 'rgba(31, 119, 180, 0.6)',
                borderColor: '#1f77b4',
                borderWidth: 1,
                maxBarThickness: 50,
                categoryPercentage: 0.8,
                barPercentage: 0.9
            }]
        };
    }, [precipitationData]);

    // Chart options configuration
    const chartOptions: ChartOptions<'bar'> = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
        },
        plugins: {
            title: {
                display: true,
                text: title,
                font: {
                    size: 16
                }
            },
            legend: {
                display: true,
                position: 'top'
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const value = context.parsed.y;
                        return `Precipitation: ${value?.toFixed(3) || 0} mm`;
                    },
                    title: (context) => {
                        if (context[0]) {
                            return moment(context[0].parsed.x).format('DD MMM YYYY HH:mm');
                        }
                        return '';
                    }
                }
            },
            // Zoom plugin configuration
            zoom: {
                zoom: {
                    wheel: {
                        enabled: true,
                    },
                    pinch: {
                        enabled: true
                    },
                    mode: 'x',
                },
                pan: {
                    enabled: true,
                    mode: 'x',
                }
            }

        },
        scales: {
            x: {
                type: 'time',
                time: {
                    displayFormats: {
                        hour: 'HH:mm',
                        day: 'DD/MM',
                        week: 'DD/MM',
                        month: 'MM/YY'
                    },
                    tooltipFormat: 'DD MMM YYYY HH:mm'
                },
                title: {
                    display: true,
                    text: 'Date'
                }
            },
            y: {
                title: {
                    display: true,
                    text: 'Precipitation (mm)'
                },
                beginAtZero: true,
                ticks: {
                    callback: function(value: any) {
                        return `${parseFloat(value).toFixed(3)} mm`;
                    }
                }
            }
        },
        elements: {
            bar: {
                borderSkipped: false,
            }
        }
    }), [title]);


    // Loading state
    if (loading) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: `${height}px` }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading precipitation data...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                </div>
            </div>
        );
    }

    // No data state
    if (!precipitationData?.data || precipitationData.data.length === 0) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="text-center text-muted" style={{ height: `${height}px`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div>
                            <i className="fa fa-chart-bar fa-3x mb-3"></i>
                            <p>No precipitation data available for the selected period</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">{title}</h5>
                    <div className="d-flex align-items-center gap-3">
                         {/* Reset zoom button */}
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={resetZoom}
                            title="Reset Zoom"
                        >
                            <i className="fa fa-search-minus me-1"></i>
                            Reset Zoom
                        </button>
                    </div>
                </div>
            </div>
            <div className="card-body">
                <div style={{ height: `${height}px` }}>
                    <Bar
                         ref={chartRef}
                         key={chartKey}
                         data={chartData}
                         options={chartOptions}
                         plugins={[dripClogAnnotationPlugin, qcAnnotationPlugin]}
                    />
                </div>
                <div className="mt-2">
                       <small className="text-muted">
                        <i className="fa fa-info-circle me-1"></i>
                        <strong>Tip:</strong> Use mouse wheel to zoom, drag to pan, or click "Reset Zoom" to return to original view
                    </small>
                    <br />
                    <small className="text-muted">
                        Data points: {precipitationData.metadata.count} / {precipitationData.metadata.total_count}
                        {precipitationData.metadata.count < precipitationData.metadata.total_count &&
                            ` (${((precipitationData.metadata.count / precipitationData.metadata.total_count) * 100).toFixed(1)}% coverage)`
                        }
                        {showDripClog && dripClogPeriods.length > 0 && (
                            <span className="ms-3 text-danger">
                                <i className="fa fa-exclamation-triangle me-1"></i>
                                {dripClogPeriods.length} drip clog period{dripClogPeriods.length !== 1 ? 's' : ''} detected
                            </span>
                        )}
                         {showQCIssues && qcPeriods.length > 0 && (
                            <span className="ms-3 text-warning">
                                <i className="fa fa-flag me-1"></i>
                                {qcPeriods.length} QC issue{qcPeriods.length !== 1 ? 's' : ''} detected
                            </span>
                        )}

                    </small>
                </div>
            </div>
        </div>
    );
};

export default PrecipitationChart;

